import { streamAsk, type HistoryTurn } from '../api/askApi';
import { useChatStore } from '../model/chatStore';
import { applyEvent, isRunning, newTurn, type Turn } from '../model/conversation';

const HISTORY_TURNS = 3;
const NO_TURNS: Turn[] = [];

// One answer streams at a time across the panel and the full page, so the controller is shared.
let current: { controller: AbortController; conversationId: string; turnId: string } | null = null;

const activeTurns = () => {
  const { conversations, activeId } = useChatStore.getState();
  return conversations.find((c) => c.id === activeId)?.turns ?? NO_TURNS;
};

export function useConversation() {
  const turns = useChatStore((s) => s.conversations.find((c) => c.id === s.activeId)?.turns ?? NO_TURNS);

  const stop = () => {
    if (!current) return;
    const { conversationId, turnId } = current;
    current.controller.abort();
    current = null;
    useChatStore.getState().updateTurn(conversationId, turnId, (t) => (isRunning(t) ? { ...t, stopped: true } : t));
  };

  const send = (question: string) => {
    stop();
    // Read at call time: a caller may have just started a new chat in the same tick.
    const history: HistoryTurn[] = activeTurns()
      .filter((t) => t.answer?.status === 'answered')
      .slice(-HISTORY_TURNS)
      .map((t) => ({ question: t.question, answer: t.answer?.text ?? '' }));
    const turn = newTurn(question);
    const conversationId = useChatStore.getState().addTurn(turn);
    const controller = new AbortController();
    current = { controller, conversationId, turnId: turn.id };
    const update = (fn: (t: Turn) => Turn) => { useChatStore.getState().updateTurn(conversationId, turn.id, fn); };

    streamAsk({ question, history }, (evt) => { update((t) => applyEvent(t, evt)); }, controller.signal)
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        update((t) => ({ ...t, error: err instanceof Error ? err.message : 'The assistant is unavailable.' }));
      })
      .finally(() => { if (current?.turnId === turn.id) current = null; });
  };

  return { turns, send, stop, isBusy: turns.some(isRunning) };
}
